import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import { ReactElement, useRef, useState, useEffect } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { LoadingIndicator } from "../../../Common/UI/components/Loading";
import styles from "./Report.module.css";
import dayjs from "dayjs";
import { GridLoadingOverlay } from "./GridLoadingOverlay";
import useFetchMsal from "../../../Common/UI/hook/useFetchMsal";
import { config } from "../../../authConfig";

interface CrunchTimeDetailReport {
  checkNumber: number;
  posTotalPrice: string | null;
  ctTotalPrice: string | null;
  totalPriceDiff: string | null;
  [key: string]: any;
}

interface VendorReportDialogProps {
  storeNumber: string | undefined;
  storeDate: string | undefined;
  handleClose: () => void;
  handleReportSelected: (report: CrunchTimeDetailReport) => void;
}

const DEFAULT_COL_DEFS: ColDef[] = [
  {
    field: "checkNumber",
    headerName: "Check Number",
    filter: "agTextColumnFilter",
    width: 150,
  },
  {
    field: "posTotalPrice",
    headerName: "POS Total Price",
    filter: "agNumberColumnFilter",
    valueFormatter: (params) => (params.value === null || params.value === undefined ? "N/A" : `$${params.value}`),
    width: 150,
  },
  {
    field: "ctTotalPrice",
    headerName: "CT Total Price",
    filter: "agNumberColumnFilter",
    valueFormatter: (params) => (params.value === null || params.value === undefined ? "N/A" : `$${params.value}`),
    width: 150,
  },
  {
    field: "totalPriceDiff",
    headerName: "Total Price Difference",
    filter: "agNumberColumnFilter",
    valueFormatter: (params) => {
      const { data } = params;
      const pos = parseFloat(data?.posTotalPrice);
      const ct = parseFloat(data?.ctTotalPrice);

      const hasPos = !isNaN(pos);
      const hasCt = !isNaN(ct);

      if (hasPos && hasCt) {
        const diff = Math.abs(pos - ct).toFixed(2);
        return `$${diff}`;
      } else if (hasPos) {
        return `$${pos.toFixed(2)}`;
      } else if (hasCt) {
        return `$${ct.toFixed(2)}`;
      } else {
        return "N/A";
      }
    },
    width: 150,
  },
];

export const GuestChecksReportDialog = ({ storeNumber, storeDate, handleClose, handleReportSelected }: VendorReportDialogProps): ReactElement => {
  const gridRef = useRef<AgGridReact>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [reports, setReports] = useState<CrunchTimeDetailReport[]>([]);

  const execute = useFetchMsal();

  useEffect(() => {
    fetchData();
  }, [storeNumber, storeDate]);

  const fetchData = async () => {
    if (!storeNumber || !storeDate) {
      console.error("Store number or date is missing");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const url = config.rootAPIList.crunchTimeDetailReport.replace("{storeNumber}", storeNumber).replace("{storeDate}", storeDate);

      const result = await execute("GET", `${config.rootAPIUrl}${url}`);
      setReports(result || []);
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportAsCsv = () => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsCsv({
        fileName: `crunch_time_report_${dayjs().format("YYYY_MM_DD_HH_mm_ss")}.csv`,
        processCellCallback: (params) => {
          const field = params.column.getColDef().field;
          const value = params.value;

          if (field === "posTotalPrice" || field === "ctTotalPrice" || field === "totalPriceDiff") {
            return value === null || value === undefined ? "N/A" : `$${value}`;
          }

          return value;
        },
      });
    }
  };

  // Format the date for display
  const formattedDate = storeDate ? new Date(storeDate).toLocaleDateString() : "N/A";

  return (
    <Dialog
      open
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          height: "80vh",
          minHeight: "800px",
          maxHeight: "900px",
          width: "90vw",
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2 }}>
        <Box>
          <DialogTitle sx={{ p: 0 }}>Guest Checks Report</DialogTitle>
          <Typography variant="subtitle1">
            Store: {storeNumber || "N/A"} | Date: {formattedDate}
          </Typography>
        </Box>
        <Box>
          <Button
            startIcon={<DownloadIcon />}
            onClick={handleExportAsCsv}
            sx={{ mr: 1, width: "190px" }}
            disabled={isLoading || reports.length === 0}
          >
            Export as CSV
          </Button>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      <DialogContent dividers sx={{ p: 0, display: "flex", flexDirection: "column" }}>
        <LoadingIndicator show={isLoading}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
            <div id={styles.eventsGrid} className="ag-theme-quartz">
              <AgGridReact
                ref={gridRef}
                columnDefs={DEFAULT_COL_DEFS}
                rowData={reports}
                loadingOverlayComponent={GridLoadingOverlay}
                rowSelection="single"
                pagination
                paginationPageSize={20}
                defaultColDef={{
                  headerClass: "tableHeader",
                  filter: "agTextColumnFilter",
                  filterParams: {
                    buttons: ["apply", "clear"],
                    closeOnApply: true,
                    maxNumConditions: 4,
                  },
                }}
                onGridReady={() => {
                  gridRef.current?.api.sizeColumnsToFit();
                }}
              />
            </div>
          </Box>
        </LoadingIndicator>
      </DialogContent>
    </Dialog>
  );
};
